// Hearth — auth context. Holds the signed-in user + token, restores the session
// from a stored token on load, and exposes login/register/logout. The token is
// attached to every API request via setAuthToken in lib/api.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, ApiError, getAuthToken, setAuthToken } from "../lib/api";
import type { AuthUser } from "../lib/types";

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore session from a stored token. Only a definitive rejection of the
  // token logs the user out — a network failure (backend restarting, flaky
  // wifi) keeps the token and retries with backoff.
  useEffect(() => {
    const token = getAuthToken();
    if (!token) { setLoading(false); return; }
    let alive = true;
    let timer: number | null = null;
    let attempt = 0;
    const restore = () => {
      api.me()
        .then((r) => {
          if (!alive) return;
          if (r.user) setUser(r.user);
          else setAuthToken(null); // server says this token maps to no session
          setLoading(false);
        })
        .catch((e) => {
          if (!alive) return;
          if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
            setAuthToken(null);
            setLoading(false);
            return;
          }
          // Transient: keep the token, retry quietly in the background.
          attempt += 1;
          setLoading(false);
          timer = window.setTimeout(restore, Math.min(1000 * 2 ** attempt, 15_000));
        });
    };
    restore();
    return () => { alive = false; if (timer !== null) window.clearTimeout(timer); };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const r = await api.login(username, password);
    setAuthToken(r.token);
    setUser(r.user);
  }, []);

  const register = useCallback(async (username: string, password: string, displayName?: string) => {
    const r = await api.register(username, password, displayName);
    setAuthToken(r.token);
    setUser(r.user);
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch { /* ignore */ }
    setAuthToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
